import { UserRound } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { errorMessage } from '../api/client';
import { useSession } from '../stores/session';
import { Button } from './ui/Button';
import { Input } from './ui/Input';

/**
 * Going on without an account: just a name. Guests get a dashboard and
 * boards like everyone else, and can save their account later.
 */
export function ContinueAsGuest({
  onDone,
  startOpen = false,
}: {
  onDone(): void;
  /** Show the name field straight away instead of a button. */
  startOpen?: boolean;
}) {
  const allowGuests = useSession((s) => s.config?.allowGuests !== false);
  const continueAsGuest = useSession((s) => s.continueAsGuest);
  const [open, setOpen] = useState(startOpen);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!allowGuests) return null;

  if (!open) {
    return (
      <Button size="lg" className="w-full" onClick={() => setOpen(true)}>
        <UserRound className="size-4" />
        Continue as guest
      </Button>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await continueAsGuest(name);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <Input
        label="Your name"
        hint="Shown to others on the boards you open. No account needed."
        autoFocus
        required
        maxLength={60}
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={error}
      />
      <Button type="submit" variant="primary" size="lg" loading={busy}>
        Continue as guest
      </Button>
    </form>
  );
}

/** "or", between two ways in. */
export function OrDivider() {
  return (
    <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
      <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
      or
      <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}
